import * as React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowSyncRegular, DismissRegular } from "@fluentui/react-icons";
import { useTestingAgentClient } from "../../api/TestingAgentApiClient";
import type { FileReference, ProjectVideoFile, SessionResponse, ActionLogEntry } from "../../api/models/ApiModels";
import { PlaywrightResultsParser } from "../../services/PlaywrightResultsParser";
import type { TestResultsModel, TestCase } from "../../models/TestResultModels";
import type { ProjectDashboardProps, TestHistoryPoint } from "../../types/ProjectDashboard.types";
import { DisplayTestStatus } from "../../types/TestCasesTab.types";
import { useProjectDashboardPageStyles } from "../../styles/projectDashboard.styles";
import { useTenant } from "../../auth/TenantContext";
import { Header } from "../Header";
import { StatsRow, type StatsData } from "../StatsRow";
import { RunHistoryChart, type RunBarData, type TimeRange } from "../RunHistoryChart";
import { TestResultsTable } from "../TestResultsTable";
import { ActionLog } from "../Sidebar";
import { ActivityLogPopup } from "../ActivityLogPopup";
import EmptyStateLanding from "../EmptyStateLanding";
import { mapRecordingsToVideoFiles, findRecordingForVideoPath } from "../../utils/recordings";
import { useVideoPlayer } from "../../utils/useVideoPlayer";
import { fetchTextWithRetry } from "../../utils/fetchJson";

const PAGE_SIZE = 25;

function getCreatedFrom(range: TimeRange): string {
    const now = new Date();
    const days = range === "1d" ? 1 : range === "7d" ? 7 : 30;
    now.setDate(now.getDate() - days);
    return now.toISOString();
}

function findTestResultsFile(files?: FileReference[] | null): FileReference | undefined {
    return files?.find((f) => f.fileType.trim().toLowerCase() === "test-results" && !!f.sasUri);
}

const ProjectDashboard: React.FC<ProjectDashboardProps> = ({ resourceId, projectId, onTitleChange }) => {
    const s = useProjectDashboardPageStyles();
    const navigate = useNavigate();
    const location = useLocation();
    const { getProject, getSessions } = useTestingAgentClient();
    const { selectedTenantId } = useTenant();

    // Save last-visited project URL for auto-redirect on next visit
    React.useEffect(() => {
        const dashboardUrl = `/dashboard/${encodeURIComponent(resourceId)}/${encodeURIComponent(projectId)}`;
        localStorage.setItem("dashboard.lastProjectUrl", dashboardUrl);
        if (selectedTenantId) {
            localStorage.setItem(`dashboard.lastProjectUrl.${selectedTenantId}`, dashboardUrl);
        }
    }, [resourceId, projectId, selectedTenantId]);

    const [projectName, setProjectName] = React.useState<string | undefined>(
        (location.state as { projectName?: string } | null)?.projectName
    );
    const [timeRange, setTimeRange] = React.useState<TimeRange>("7d");
    const [refreshKey, setRefreshKey] = React.useState(0);
    const [appUrl, setAppUrl] = React.useState<string | null>(null);

    const [sessions, setSessions] = React.useState<SessionResponse[]>([]);
    const [selectedSession, setSelectedSession] = React.useState<SessionResponse | null>(null);
    const [testResults, setTestResults] = React.useState<TestResultsModel | null>(null);
    const [videoFiles, setVideoFiles] = React.useState<ProjectVideoFile[]>([]);
    const { playRecording, modal: videoModal, close: closeVideo } = useVideoPlayer(projectId);
    const [sessionStats, setSessionStats] = React.useState<Map<string, { passed: number; failed: number; flaky: number; skipped: number }>>(new Map());
    // sessionId -> Map<historyKey, DisplayTestStatus>
    const [sessionResults, setSessionResults] = React.useState<Map<string, Map<string, DisplayTestStatus>>>(new Map());
    const [loading, setLoading] = React.useState(true);
    const [refreshing, setRefreshing] = React.useState(false);
    const [loadingMore, setLoadingMore] = React.useState(false);
    const [resultsLoading, setResultsLoading] = React.useState(false);
    const [hasEverHadSessions, setHasEverHadSessions] = React.useState(false);
    const prevProjectKey = React.useRef(`${resourceId}/${projectId}`);
    const [actionLogs, setActionLogs] = React.useState<ActionLogEntry[]>([]);
    const [actionLogsLoading, setActionLogsLoading] = React.useState(false);
    // Popup keeps its own filtered fetch so typing in the popup search box
    // doesn't reshape the always-visible sidebar feed. See the popup-only
    // effect below.
    const [popupActionLogs, setPopupActionLogs] = React.useState<ActionLogEntry[]>([]);
    const [popupLogsLoading, setPopupLogsLoading] = React.useState(false);
    const [activityPopupOpen, setActivityPopupOpen] = React.useState(false);
    // Activity-log search & filter — owned here so the popup's controlled
    // inputs persist across open/close.
    const [activitySearch, setActivitySearch] = React.useState("");
    const [activityFilter, setActivityFilter] = React.useState<"all" | "scout" | "heal">("all");
    const [debouncedSearch, setDebouncedSearch] = React.useState("");
    React.useEffect(() => {
        const t = window.setTimeout(() => setDebouncedSearch(activitySearch.trim()), 300);
        return () => window.clearTimeout(t);
    }, [activitySearch]);
    const [error, setError] = React.useState<string | null>(null);
    const [healSessions, setHealSessions] = React.useState<SessionResponse[]>([]);
    const [scoutSessions, setScoutSessions] = React.useState<SessionResponse[]>([]);

    React.useEffect(() => {
        const projectKey = `${resourceId}/${projectId}`;
        const isNewProject = prevProjectKey.current !== projectKey;
        prevProjectKey.current = projectKey;

        let cancelled = false;
        const load = async () => {
            if (isNewProject) {
                setLoading(true);
                setSessions([]);
                setSessionStats(new Map());                
                setSessionResults(new Map());                
                setSelectedSession(null);
                setHasEverHadSessions(false);
            } else {
                setRefreshing(true);
            }
            setLoadingMore(false);
            setError(null);
            try {
                if (isNewProject || !projectName || appUrl === null) {
                    let project: Awaited<ReturnType<typeof getProject>> | null = null;
                    for (let attempt = 0; attempt < 3; attempt++) {
                        try {
                            project = await getProject(resourceId, projectId);
                            break;
                        } catch {
                            if (attempt < 2) await new Promise(r => setTimeout(r, 1000));
                        }
                    }
                    if (cancelled) return;
                    if (project) {
                        onTitleChange?.(project.name ?? "Project");
                        setProjectName(project.name ?? undefined);
                        setAppUrl(project.appConnectionDetails?.appUrl ?? null);
                    }
                }

                // Check if the project has ANY sessions (regardless of time range)
                // so we only show the empty state for truly empty projects.
                if (isNewProject || !hasEverHadSessions) {
                    try {
                        const probe = await getSessions(resourceId, projectId, { top: 1 });
                        if (cancelled) return;
                        if ((probe.value ?? []).length > 0) setHasEverHadSessions(true);
                    } catch { /* fall through – let the main fetch handle errors */ }
                }

                const createdFrom = getCreatedFrom(timeRange);
                let allSessions: SessionResponse[] = [];
                let contToken: string | null | undefined = undefined;
                let firstBatch = true;
                // Fetch page by page until no more continuation tokens
                while (true) {
                    if (cancelled) return;
                    if (!firstBatch) setLoadingMore(true);
                    const page = await getSessions(resourceId, projectId, {
                        top: PAGE_SIZE,
                        createdFrom,
                        continuationToken: contToken ?? undefined,
                        commandTypes: ["run"],
                        hasTestResults: true,
                    });
                    if (cancelled) return;

                    const newSessions = page.value ?? [];
                    // only show sessions with commandType "run". All the sessions are used for action logs
                    const sessionsToShow = newSessions.filter(s => s.commandType.trim().toLowerCase() === "run");
                    allSessions = [...allSessions, ...sessionsToShow];

                    // Sort newest first
                    const sorted = [...allSessions].sort(
                        (a, b) => new Date(b.completedDateTime ?? b.createdDateTime).getTime() -
                                  new Date(a.completedDateTime ?? a.createdDateTime).getTime(),
                    );
                    setSessions(sorted);
                    
                    if (sorted.length > 0) setHasEverHadSessions(true);

                    if (firstBatch) {
                        const first = sorted.find((sess) => !!findTestResultsFile(sess.files));
                        setSelectedSession(first ?? null);
                        setLoading(false);
                        setActionLogsLoading(false);
                        setRefreshing(false);
                        firstBatch = false;
                    }

                    // Fetch stats for this batch in parallel
                    const batchStatsEntries: [string, { passed: number; failed: number; flaky: number; skipped: number }][] = [];
                    const batchResultsEntries: [string, Map<string, DisplayTestStatus>][] = [];
                    await Promise.all(newSessions.map(async (sess) => {
                        const f = findTestResultsFile(sess.files);
                        if (!f?.sasUri) return;
                        try {
                            const res = await fetch(f.sasUri);
                            if (!res.ok) return;
                            const text = await res.text();
                            const jsonStart = text.indexOf('{');
                            if (jsonStart < 0) return;
                            const raw = JSON.parse(text.slice(jsonStart));
                            const parsed = PlaywrightResultsParser.parseTestResults(raw);
                            if (!parsed.success || !parsed.data) return;
                            const all = parsed.data.allTestCases;
                            const passed = all.filter(t => t.displayStatus === DisplayTestStatus.Passed).length;
                            const failed = all.filter(t => t.displayStatus === DisplayTestStatus.Failed).length;
                            const flaky = all.filter(t => t.displayStatus === DisplayTestStatus.Flaky).length;
                            batchStatsEntries.push([sess.sessionId, { passed, failed, flaky, skipped: all.length - passed - failed - flaky }]);
                            const tcMap = new Map<string, DisplayTestStatus>();
                            for (const tc of all) tcMap.set(tc.historyKey, tc.displayStatus);
                            batchResultsEntries.push([sess.sessionId, tcMap]);
                        } catch { /* ignore */ }
                    }));
                    if (cancelled) return;
                    if (batchStatsEntries.length > 0) {
                        setSessionStats(prev => {
                            const next = new Map(prev);
                            for (const [id, stats] of batchStatsEntries) next.set(id, stats);
                            return next;
                        });
                    }
                    if (batchResultsEntries.length > 0) {
                        setSessionResults(prev => {
                            const next = new Map(prev);
                            for (const [id, tcMap] of batchResultsEntries) next.set(id, tcMap);
                            return next;
                        });
                    }

                    if (!page.continuationToken) break;
                    contToken = page.continuationToken;
                }

                // If no sessions found with current time filter, check if any exist at all
                if (allSessions.length === 0) {
                    try {
                        const check = await getSessions(resourceId, projectId, { top: 1 });
                        if (!cancelled && (check.value ?? []).length > 0) {
                            setHasEverHadSessions(true);
                        }
                    } catch { /* ignore — the main query already handles errors */ }
                }
            } catch (e) {
                if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
            } finally {
                if (!cancelled) { setLoading(false); setRefreshing(false); setLoadingMore(false); }
            }
        };
        void load();
        return () => { cancelled = true; };
    }, [resourceId, projectId, timeRange, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // Sidebar action-log feed: ALWAYS unfiltered (no search / no tag filter).
    // The popup has its own filtered fetch below so typing in the popup
    // search box doesn't reshape this list. Also drives heal/scoutSessions
    // because the "View Heal Report" / "View Scout Report" buttons hang off
    // the sidebar feed.
    React.useEffect(() => {
        const projectKey = `${resourceId}/${projectId}`;
        const isNewProject = prevProjectKey.current !== projectKey;
        prevProjectKey.current = projectKey;
        setActionLogs([]);
        let cancelled = false;
        const loadLogs = async () => {
            if(isNewProject) {
                setActionLogs([]);
                setHealSessions([]);
                setScoutSessions([]);
            }
            setActionLogsLoading(true);
            try {
                const res = await getSessions(resourceId, projectId, {
                    top: PAGE_SIZE,
                    createdFrom: getCreatedFrom(timeRange),
                    commandTypes: ["scout", "heal"],
                    hasActions: true,
                });
                if (cancelled) return;
                const logs = res.value?.flatMap(s => s.actionLogs ?? []) ?? [];
                setActionLogs(logs.sort((a, b) => new Date(b.createdDateTime).getTime() - new Date(a.createdDateTime).getTime()));
                // Store heal sessions that have HTML reports for the "View Report" button
                let allHealSessions = (res.value ?? []).filter(
                    s => s.commandType.trim().toLowerCase() === "heal" &&
                         s.files?.some(f => f.fileType.trim().toLowerCase() === "heal-report" && f.sasUri)
                );
                setHealSessions(allHealSessions);
                // Same for scout sessions — needed by the "View Scout Report" button.
                let allScoutSessions = (res.value ?? []).filter(
                    s => s.commandType.trim().toLowerCase() === "scout" &&
                         s.files?.some(f => f.fileType.trim().toLowerCase() === "scout-report" && f.sasUri)
                );
                setScoutSessions(allScoutSessions);
                let allActionLogs: ActionLogEntry[] = logs;
                let contToken: string | null | undefined = res.continuationToken;
                while (contToken) {
                    if (cancelled) return;
                    const nextRes = await getSessions(resourceId, projectId, {
                        top: PAGE_SIZE,
                        createdFrom: getCreatedFrom(timeRange),
                        continuationToken: contToken,
                        commandTypes: ["scout", "heal"],
                        hasActions: true,
                    });
                    const nextLogs = nextRes.value?.flatMap(s => s.actionLogs ?? []) ?? [];
                    allActionLogs = [...allActionLogs, ...nextLogs];
                    setActionLogs(allActionLogs.sort((a, b) => new Date(b.createdDateTime).getTime() - new Date(a.createdDateTime).getTime()));
                    const nextHealSessions = (nextRes.value ?? []).filter(
                        s => s.commandType.trim().toLowerCase() === "heal" &&
                             s.files?.some(f => f.fileType.trim().toLowerCase() === "heal-report" && f.sasUri)
                    );
                    allHealSessions = [...allHealSessions, ...nextHealSessions];
                    setHealSessions(allHealSessions);
                    const nextScoutSessions = (nextRes.value ?? []).filter(
                        s => s.commandType.trim().toLowerCase() === "scout" &&
                             s.files?.some(f => f.fileType.trim().toLowerCase() === "scout-report" && f.sasUri)
                    );
                    allScoutSessions = [...allScoutSessions, ...nextScoutSessions];
                    setScoutSessions(allScoutSessions);
                    contToken = nextRes.continuationToken;
                }

            } catch {
                if (!cancelled) setActionLogs([]);
            } finally {
                if (!cancelled) setActionLogsLoading(false);
            }
        };
        void loadLogs();
        return () => { cancelled = true; };
        }, [resourceId, projectId, timeRange]); // eslint-disable-line react-hooks/exhaustive-deps

    // Popup-only filtered feed. Runs only while the popup is open so the
    // sidebar never reflects the popup's search/filter state.
    React.useEffect(() => {
        if (!activityPopupOpen) return;
        let cancelled = false;
        const loadFiltered = async () => {
            setPopupLogsLoading(true);
            try {
                // Stored Action values are capitalized ("Scout"/"Heal") and the Cosmos
                // IN filter on the dataplane is case-sensitive, so send canonical case.
                const actionTypes = activityFilter === "all" ? ["Scout", "Heal"] : [activityFilter === "scout" ? "Scout" : "Heal"];
                const search = debouncedSearch || undefined;
                const res = await getSessions(resourceId, projectId, {
                    top: PAGE_SIZE,
                    createdFrom: getCreatedFrom(timeRange),
                    commandTypes: ["scout", "heal"],
                    hasActions: true,
                    actionTypes,
                    search,
                });
                if (cancelled) return;
                const logs = res.value?.flatMap(s => s.actionLogs ?? []) ?? [];
                setPopupActionLogs(logs.sort((a, b) => new Date(b.createdDateTime).getTime() - new Date(a.createdDateTime).getTime()));
                let allActionLogs: ActionLogEntry[] = logs;
                let contToken: string | null | undefined = res.continuationToken;
                while (contToken) {
                    if (cancelled) return;
                    const nextRes = await getSessions(resourceId, projectId, {
                        top: PAGE_SIZE,
                        createdFrom: getCreatedFrom(timeRange),
                        continuationToken: contToken,
                        commandTypes: ["scout", "heal"],
                        hasActions: true,
                        actionTypes,
                        search,
                    });
                    const nextLogs = nextRes.value?.flatMap(s => s.actionLogs ?? []) ?? [];
                    allActionLogs = [...allActionLogs, ...nextLogs];
                    setPopupActionLogs(allActionLogs.sort((a, b) => new Date(b.createdDateTime).getTime() - new Date(a.createdDateTime).getTime()));
                    contToken = nextRes.continuationToken;
                }
            } catch {
                if (!cancelled) setPopupActionLogs([]);
            } finally {
                if (!cancelled) setPopupLogsLoading(false);
            }
        };
        void loadFiltered();
        return () => { cancelled = true; };
    }, [activityPopupOpen, resourceId, projectId, timeRange, debouncedSearch, activityFilter]); // eslint-disable-line react-hooks/exhaustive-deps



    React.useEffect(() => {
        if (!selectedSession) {
            setTestResults(null);
            setVideoFiles([]);
            return;
        }
        setVideoFiles(mapRecordingsToVideoFiles(selectedSession.recordings));
        closeVideo();

        const file = findTestResultsFile(selectedSession.files);
        if (!file?.sasUri) { setTestResults(null); return; }

        const ctrl = new AbortController();
        setResultsLoading(true);
        fetchTextWithRetry(file.sasUri, ctrl.signal)
            .then(text => {
                const jsonStart = text.indexOf('{');
                if (jsonStart < 0) { setTestResults(null); return; }
                const raw = JSON.parse(text.slice(jsonStart));
                const parsed = PlaywrightResultsParser.parseTestResults(raw);
                const model = parsed.success ? (parsed.data ?? null) : null;
                setTestResults(model);
            })
            .catch((err) => {
                if (ctrl.signal.aborted) return;
                console.error("[ProjectDashboard] test-results.json fetch failed", err);
                setTestResults(null);
            })
            .finally(() => { if (!ctrl.signal.aborted) setResultsLoading(false); });
        return () => ctrl.abort();
    }, [selectedSession]);

    const runBars: RunBarData[] = React.useMemo(() =>
        [...sessions].reverse().map((sess, i) => {
            const stats = sessionStats.get(sess.sessionId);
            return {
                id: sess.sessionId,
                label: `#${sessions.length - i}`,
                passed: stats?.passed ?? 0,
                failed: stats?.failed ?? 0,
                skipped: stats?.skipped ?? 0,
                flaky: stats?.flaky ?? 0,
                timestamp: sess.completedDateTime ?? sess.createdDateTime,
            };
        }),
    [sessions, sessionStats]);

    const enrichedBars = runBars;

    // Build cross-run history map: historyKey -> TestHistoryPoint[] (oldest-first, up to 10 previous runs)
    const crossRunHistory = React.useMemo((): Map<string, TestHistoryPoint[]> => {
        const map = new Map<string, TestHistoryPoint[]>();
        const selectedIdx = selectedSession
            ? sessions.findIndex(s => s.sessionId === selectedSession.sessionId)
            : -1;
        // sessions is newest-first; older = higher index
        const olderSessions = selectedIdx >= 0 ? sessions.slice(selectedIdx + 1) : sessions;
        // oldest-first, capped at 10
        const relevant = [...olderSessions].reverse().slice(-10);
        for (const sess of relevant) {
            const tcMap = sessionResults.get(sess.sessionId);
            if (!tcMap) continue;
            for (const [historyKey, status] of tcMap) {
                if (!map.has(historyKey)) map.set(historyKey, []);
                map.get(historyKey)!.push({
                    runId: sess.sessionId,
                    completedAt: sess.completedDateTime ?? sess.createdDateTime,
                    status,
                    duration: 0,
                });
            }
        }
        return map;
    }, [sessions, selectedSession, sessionResults]);

    const stats: StatsData = React.useMemo(() => {
        if (!testResults) return { totalTests: 0, totalSuites: 0, passed: 0, skipped: 0, failed: 0, flaky: 0 };

        // Find previous session's stats (sessions is newest-first)
        let previousRun: StatsData["previousRun"] | undefined;
        if (selectedSession) {
            const idx = sessions.findIndex(s => s.sessionId === selectedSession.sessionId);
            if (idx >= 0 && idx < sessions.length - 1) {
                const prevSession = sessions[idx + 1];
                const prevStats = sessionStats.get(prevSession.sessionId);
                if (prevStats) {
                    previousRun = {
                        passed: prevStats.passed,
                        failed: prevStats.failed,
                        skipped: prevStats.skipped,
                        flaky: prevStats.flaky,
                    };
                }
            }
        }

        return {
            totalTests: testResults.totalTestCount,
            totalSuites: testResults.groupedDisplaySuites.length,
            passed: testResults.passedCount,
            skipped: testResults.skippedCount,
            failed: testResults.failedCount,
            flaky: testResults.flakyCount,
            previousRun,
        };
    }, [testResults, selectedSession, sessions, sessionStats]);

    const handleWatchVideo = (tc: TestCase) => {
        const match = findRecordingForVideoPath(tc.primaryVideoPath, videoFiles);
        playRecording(match, tc.title);
    };

    const handleViewHealReport = React.useCallback((entry: ActionLogEntry) => {
        // Find the heal session that owns this action log entry
        const session = healSessions.find(hs =>
            hs.actionLogs?.some(al =>
                al.title === entry.title && al.createdDateTime === entry.createdDateTime
            )
        );
        if (session) {
            // Navigate to the dedicated heal report route. resourceId/projectId travel as
            // query params because resourceId is an ARM ID with embedded slashes.
            const params = new URLSearchParams({ resourceId, projectId });
            navigate(`/heal/${encodeURIComponent(session.sessionId)}?${params.toString()}`);
        }
    }, [healSessions, navigate, resourceId, projectId]);

    const handleViewScoutReport = React.useCallback((entry: ActionLogEntry) => {
        // Mirror of handleViewHealReport for scout sessions.
        const session = scoutSessions.find(ss =>
            ss.actionLogs?.some(al =>
                al.title === entry.title && al.createdDateTime === entry.createdDateTime
            )
        );
        if (session) {
            const params = new URLSearchParams({ resourceId, projectId });
            navigate(`/scout/${encodeURIComponent(session.sessionId)}?${params.toString()}`);
        }
    }, [scoutSessions, navigate, resourceId, projectId]);

    if (loading) return <div className={s.center}>Loading…</div>;
    if (error) return <div className={s.center} style={{ color: "#f85149" }}>{error}</div>;

    if (sessions.length === 0 && !hasEverHadSessions) {
        return (
            <div className={s.page}>
                <Header
                    projectName={projectName}
                    resourceId={resourceId}
                    projectId={projectId}
                    onBack={() => navigate(`/?resourceId=${encodeURIComponent(resourceId)}`, { state: { fromBack: true } })}
                />
                <div className={s.emptyBody}>
                    <div className={s.emptyMainContent}>
                        <div className={s.titleRow}>
                            <span className={s.titleText}>Test Dashboard</span>
                            <div className={s.titleSpacer} />
                            <button className={s.refreshBtn} onClick={() => setRefreshKey(k => k + 1)}>
                                <ArrowSyncRegular style={{ fontSize: 16 }} /> Refresh
                            </button>
                            {appUrl && (
                                <a className={s.openAppBtn} href={appUrl} target="_blank" rel="noopener noreferrer">
                                    Open app
                                </a>
                            )}
                        </div>
                        <EmptyStateLanding />
                    </div>
                    <EmptyStateLanding.Sidebar />
                </div>
            </div>
        );
    }

    return (
        <div className={s.page}>
            <Header
                projectName={projectName}
                resourceId={resourceId}
                projectId={projectId}
                onBack={() => navigate(`/?resourceId=${encodeURIComponent(resourceId)}`, { state: { fromBack: true } })}
            />
            <div className={s.body}>
                <div className={s.mainContent}>
                    <div className={s.titleRow}>
                        <span className={s.titleText}>Test Dashboard</span>
                        <div className={s.titleSpacer} />
                        <button className={s.refreshBtn} onClick={() => setRefreshKey(k => k + 1)}>
                            <ArrowSyncRegular style={{ fontSize: 16 }} /> Refresh
                        </button>
                        {appUrl && (
                            <a className={s.openAppBtn} href={appUrl} target="_blank" rel="noopener noreferrer">
                                Open app
                            </a>
                        )}
                    </div>
                    <RunHistoryChart
                        runs={enrichedBars}
                        selectedRunId={selectedSession?.sessionId}
                        timeRange={timeRange}
                        onTimeRangeChange={setTimeRange}
                        loadingMore={loadingMore}
                        refreshing={refreshing}
                        onSelectRun={(id) => {
                            const sess = sessions.find(ss => ss.sessionId === id);
                            if (sess) setSelectedSession(sess);
                        }}
                    />
                    {selectedSession && (
                        <div className={s.viewingDetails}>
                            <span>
                                Viewing details for – <strong>Run#{(() => {
                                    const ts = selectedSession.completedDateTime ?? selectedSession.createdDateTime;
                                    if (!ts) return "";
                                    const d = new Date(ts);
                                    return d.toLocaleString("en-US", { month: "short", day: "numeric" }) + " – " + d.toLocaleString("en-US", { hour: "numeric", minute: "2-digit" });
                                })()}</strong>
                            </span>
                            <button
                                onClick={() => setSelectedSession(null)}
                                aria-label="Clear run selection"
                                style={{ border: "none", background: "none", cursor: "pointer", color: "inherit", display: "inline-flex", alignItems: "center", padding: 2 }}
                            >
                                <DismissRegular style={{ fontSize: 14 }} />
                            </button>
                        </div>
                    )}
                    <StatsRow data={stats} />
                    <div className={s.tableArea}>
                    {testResults && (
                        <div style={{ opacity: resultsLoading ? 0.4 : 1, transition: "opacity 0.15s" }}>
                        <TestResultsTable
                            suites={testResults.groupedDisplaySuites}
                            onWatchVideo={handleWatchVideo}
                            videoFiles={videoFiles}
                            crossRunHistory={crossRunHistory}
                        />
                        </div>
                    )}
                    {resultsLoading && !testResults && <div className={s.center}>Loading test results…</div>}
                    </div>
                </div>
                <div className={s.sidebar}>
                    <ActionLog entries={actionLogs} loading={actionLogsLoading} onViewAll={() => setActivityPopupOpen(true)} onViewHealReport={handleViewHealReport} onViewScoutReport={handleViewScoutReport} />
                </div>
            </div>

            {videoModal}

            {activityPopupOpen && (
                <ActivityLogPopup
                    items={popupActionLogs}
                    search={activitySearch}
                    onSearchChange={setActivitySearch}
                    filter={activityFilter}
                    onFilterChange={setActivityFilter}
                    loading={popupLogsLoading}
                    onClose={() => setActivityPopupOpen(false)}
                    onViewHealReport={handleViewHealReport}
                    onViewScoutReport={handleViewScoutReport}
                />
            )}

        </div>
    );
};

export default ProjectDashboard;
